import { mimeTypes } from 'client/constants/editorSettings'
import React, { useRef, useState } from 'react'
import { XCircleFillIcon } from 'src/editor/shared/icons/x-circle-fill-icon'
import { GhostIconButton, TextButton } from 'src/editor/shared/ui-lib/buttons'
import { ImageCropper } from 'src/editor/shared/ui-lib/image-cropper'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'

interface BlogPostImageInputProps {
  imageSrc?: string
  onCropComplete: (blobUrl: string, blob: Blob) => void
  onRemove: () => void
}

export const BlogPostImageInput = ({
  imageSrc,
  onCropComplete,
  onRemove,
}: BlogPostImageInputProps) => {
  const { t } = useTypedTranslation(['client'])
  const inputRef = useRef<HTMLInputElement>(null)
  const [pendingFile, setPendingFile] = useState<File>()
  const [isCropperOpen, setIsCropperOpen] = useState(false)

  const openFilePicker = () => inputRef.current?.click()

  const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0]
    if (!selectedFile) {
      return
    }

    setPendingFile(selectedFile)
    setIsCropperOpen(true)
    event.currentTarget.value = ''
  }

  const handleCropComplete = (blobUrl: string, blob: Blob) => {
    onCropComplete(blobUrl, blob)
    setIsCropperOpen(false)
    setPendingFile(undefined)
  }

  const handleCropperClose = () => {
    setIsCropperOpen(false)
    setPendingFile(undefined)
  }

  return (
    <>
      {imageSrc ? (
        <div className="relative inline-block">
          <img
            src={imageSrc}
            alt="Blog post"
            className="block h-[124px] w-[124px] rounded-[6px] object-cover"
          />
          <GhostIconButton
            onClick={onRemove}
            icon={<XCircleFillIcon className="h-5 w-5" />}
            className="absolute -top-[5px] left-[109px] size-5 min-h-0 min-w-0 overflow-hidden rounded-full border-none bg-white p-0 shadow-sm hover:opacity-80"
          />
        </div>
      ) : (
        /* ── Upload trigger area ── */
        <div className="flex min-h-[88px] flex-col rounded-[6px] border border-border200 border-dashed">
          <div className="flex w-full flex-col items-center justify-center py-6">
            <div className="mb-1 flex items-center justify-center gap-1 text-M text-textPrimary">
              <span>{t('core.file_manager.upload.drag_file')}</span>
              <TextButton onClick={openFilePicker}>
                {t('core.file_manager.upload.click_to_upload')}
              </TextButton>
            </div>
            <div className="text-S text-textTertiary">PNG, JPG, GIF, WEBP</div>
            <input
              style={{ display: 'none' }}
              type="file"
              accept={mimeTypes.image.join(' ')}
              ref={inputRef}
              onChange={handleFileSelection}
            />
          </div>
        </div>
      )}

      <ImageCropper
        imageFile={pendingFile}
        isOpened={isCropperOpen}
        onClose={handleCropperClose}
        onCropComplete={handleCropComplete}
        imageCropperProps={{
          aspectRatio: 1,
        }}
      />
    </>
  )
}
